import { useState } from "preact/compat"
import { useLocation } from "wouter-preact"
import { Form } from "@/components/form/Form.tsx"
import { useFormState } from "@/components/form/form_state.tsx"
import { Input } from "@/components/form/Input.tsx"
import { Button } from "@/components/ui/button.tsx"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import { connectSageIntacct } from "@/api/integration"

interface SageConnectFormProps {
  /** Where to go once the connection is proved and stored. */
  redirectTo: string
}

/**
 * Connect Sage Intacct with the client credentials grant.
 *
 * There is no Sage sign-in screen here on purpose. The company authorizes MarginWiz once, in its own
 * settings, by pairing our Client ID with a Web Services user — so all this form needs is which user,
 * in which company. The previous flow sent people to Sage to sign in and click Accept, which requires
 * Full Admin and cost one brand three weeks of back-and-forth.
 */
export function SageConnectForm({ redirectTo }: SageConnectFormProps) {
  const [, setLocation] = useLocation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const form = useFormState({
    company_id: "",
    web_services_user_id: "",
    entity_id: "",
  })

  async function onSubmit() {
    setLoading(true)
    setError(null)
    const res = await connectSageIntacct({
      company_id: form.company_id.value,
      web_services_user_id: form.web_services_user_id.value,
      entity_id: form.entity_id.value || undefined,
    })
    setLoading(false)
    if (!res.ok) {
      // The server proves the connection before storing it, so this message names the Sage setup
      // step that is missing. Show it verbatim rather than a generic failure.
      setError(res.error.message)
      return
    }
    setLocation(redirectTo)
  }

  return (
    <CenterColumnContainer>
      <Form class="space-y-6" onSubmit={onSubmit} alerts={form.form_alerts}>
        <h1 class="text-2xl font-semibold text-gray-900 text-center">Connect Sage Intacct</h1>
        <div class="text-sm text-gray-500 space-y-2">
          <p>First, have a Sage Intacct admin authorize MarginWiz in your company:</p>
          <ol class="list-decimal list-inside space-y-1">
            <li>
              Company → Setup → Company → Edit → Security → <strong>Web Services Authorizations</strong>, add sender ID{" "}
              <code class="text-gray-700">marginwizMPP</code>, Status Active.
            </li>
            <li>
              On the same tab, under <strong>Authorized Client Applications</strong>, add MarginWiz's Client ID and the
              Web Services user you want the integration to run as.
            </li>
          </ol>
          <p>Then tell us which user and company to connect as. Nobody needs to sign in.</p>
        </div>
        <Input
          label="Company ID"
          type="text"
          name="company_id"
          required={true}
          bind={form.company_id}
          placeholder="The ID you type at Sage's login screen"
        />
        <Input
          label="Web Services user ID"
          type="text"
          name="web_services_user_id"
          required={true}
          bind={form.web_services_user_id}
          placeholder="e.g. MarginWiz"
        />
        <Input
          label="Entity ID (optional)"
          type="text"
          name="entity_id"
          bind={form.entity_id}
          placeholder="Only for multi-entity companies"
        />
        {error && <p class="text-sm text-red-600">{error}</p>}
        <Button type="submit" class="w-full" disabled={loading}>
          {loading ? "Checking the connection…" : "Connect"}
        </Button>
        <p class="text-xs text-gray-400">
          We test the connection against your chart of accounts before saving it, so this button tells you straight away
          if something in Sage still needs changing.
        </p>
      </Form>
    </CenterColumnContainer>
  )
}
